import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Shield, DollarSign, Clock, Users, CheckCircle, Zap, BarChart3, Activity } from 'lucide-react';
import { violationDetectionService } from '../../services/violationDetectionService';

import ViolationPatternChart from './components/ViolationPatternChart';
import ClientRiskMatrix from './components/ClientRiskMatrix';
import PredictiveAnalyticsPanel from './components/PredictiveAnalyticsPanel';
import PreventionMetricsCard from './components/PreventionMetricsCard';
import OneClickRemediationPanel from './components/OneClickRemediationPanel';
import RealtimeDetectionFeed from './components/RealtimeDetectionFeed';

const ViolationAnalyticsPreventionDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [riskScores, setRiskScores] = useState([]);
  const [forecasts, setForecasts] = useState([]);
  const [preventionMetrics, setPreventionMetrics] = useState([]);
  const [remediationWorkflows, setRemediationWorkflows] = useState([]);
  const [detectionLogs, setDetectionLogs] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('30days');
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    loadDashboardData();
    setupRealtimeSubscription();

    return () => {
      if (realtimeChannel) {
        violationDetectionService?.unsubscribeFromViolationDetections(realtimeChannel);
      }
    };
  }, [selectedPeriod]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getPeriodDates(selectedPeriod);

      const [summary, patternsData, riskData, forecastsData, metricsData, workflowsData, logsData] = await Promise.all([
        violationDetectionService?.getViolationAnalyticsSummary(startDate, endDate),
        violationDetectionService?.getAllPatterns(),
        violationDetectionService?.getAllClientRiskScores(),
        violationDetectionService?.getAllForecasts(),
        violationDetectionService?.getPreventionMetrics(),
        violationDetectionService?.getReadyRemediationWorkflows(),
        violationDetectionService?.getAllDetectionLogs()
      ]);

      setAnalyticsSummary(summary);
      setPatterns(patternsData || []);
      setRiskScores(riskData || []);
      setForecasts(forecastsData || []);
      setPreventionMetrics(metricsData || []);
      setRemediationWorkflows(workflowsData || []);
      setDetectionLogs(logsData?.slice(0, 10) || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = violationDetectionService?.subscribeToViolationDetections((newDetection) => {
      setDetectionLogs(prev => [newDetection, ...prev?.slice(0, 9)]);
      loadDashboardData();
    });
    setRealtimeChannel(channel);
  };

  const getPeriodDates = (period) => {
    const endDate = new Date()?.toISOString()?.split('T')?.[0];
    let startDate;

    switch (period) {
      case '7days':
        startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
        break;
      case '30days':
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
        break;
      case '90days':
        startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
        break;
      default:
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
    }

    return { startDate, endDate };
  };

  const handleRemediationExecute = async (workflowId) => {
    try {
      await violationDetectionService?.executeOneClickRemediation(workflowId, 'current_user');
      await loadDashboardData();
    } catch (error) {
      console.error('Error executing remediation:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading violation analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Shield className="h-8 w-8 text-blue-600" />
                Violation Analytics & Prevention Dashboard
              </h1>
              <p className="mt-2 text-gray-600">
                Real-time PNG Employment Act violation detection with predictive analytics and one-click remediation
              </p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="7days">Last 7 Days</option>
                <option value="30days">Last 30 Days</option>
                <option value="90days">Last 90 Days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Prevention Metrics Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <PreventionMetricsCard
            title="Violation Frequency"
            value={analyticsSummary?.totalViolations || 0}
            trend={analyticsSummary?.violationsPrevented > analyticsSummary?.totalViolations ? 'down' : 'up'}
            icon={AlertTriangle}
            color="red"
          />
          <PreventionMetricsCard
            title="Prevention Success Rate"
            value={`${analyticsSummary?.preventionSuccessRate?.toFixed(1) || 0}%`}
            trend="up"
            icon={CheckCircle}
            color="green"
          />
          <PreventionMetricsCard
            title="Cost Savings (Kina)"
            value={`K${analyticsSummary?.totalCostAvoidedKina?.toLocaleString() || 0}`}
            trend="up"
            icon={DollarSign}
            color="blue"
          />
          <PreventionMetricsCard
            title="Avg Detection Time"
            value={`${analyticsSummary?.avgDetectionTimeHours?.toFixed(1) || 0}h`}
            trend="down"
            icon={Clock}
            color="purple"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Analytics (8 cols equivalent) */}
          <div className="lg:col-span-2 space-y-8">
            {/* Violation Pattern Analysis */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="h-6 w-6 text-blue-600" />
                  Violation Pattern Analysis
                </h2>
              </div>
              <ViolationPatternChart patterns={patterns} />
            </motion.div>

            {/* Client Risk Scoring Matrix */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Users className="h-6 w-6 text-orange-600" />
                  Client Risk Assessment Matrix
                </h2>
              </div>
              <ClientRiskMatrix riskScores={riskScores} />
            </motion.div>

            {/* Predictive Analytics */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Activity className="h-6 w-6 text-purple-600" />
                  Predictive Violation Modeling
                </h2>
              </div>
              <PredictiveAnalyticsPanel forecasts={forecasts} />
            </motion.div>
          </div>

          {/* Right Column - Actions & Alerts (4 cols equivalent) */}
          <div className="space-y-8">
            {/* Real-time Detection Feed */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-600" />
                  Real-time Detections
                </h2>
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <span className="h-2 w-2 bg-green-600 rounded-full animate-pulse"></span>
                  Live
                </span>
              </div>
              <RealtimeDetectionFeed detections={detectionLogs} />
            </motion.div>

            {/* One-Click Remediation Panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-green-600" />
                  One-Click Remediation
                </h2>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                  {remediationWorkflows?.length} Ready
                </span>
              </div>
              <OneClickRemediationPanel
                workflows={remediationWorkflows}
                onExecute={handleRemediationExecute}
              />
            </motion.div>
          </div>
        </div>

        {/* Cost-Benefit Analysis */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-green-600" />
            Cost-Benefit Analysis & ROI Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {preventionMetrics?.slice(0, 3)?.map((metric, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-2">{metric?.metricPeriod}</div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Prevention Cost:</span>
                    <span className="font-semibold text-gray-900">K{metric?.preventionCostKina?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Cost Avoided:</span>
                    <span className="font-semibold text-green-600">K{metric?.violationCostAvoidedKina?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-700">Net Savings:</span>
                    <span className="font-bold text-blue-600">K{metric?.netSavingsKina?.toLocaleString()}</span>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">ROI:</span>
                      <span className="text-lg font-bold text-green-600">{metric?.roiPercentage?.toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ViolationAnalyticsPreventionDashboard;